import React, { useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { BuilderComponent, builder } from '@builder.io/react';
import { getBuilderData } from '../builder/api/builderContent';
import { normalizeUrl } from '../utils/general';
const { isPageActive } = require('../api/gatsbyNode.utils');

const MODEL_CONFIG = {
  page: {
    urlField: 'url'
  },
  'landing-page': {
    urlField: 'pageUrl',
    queryField: 'landingPage'
  }
};

const findSSGContent = (data, url, model) => {
  const config = MODEL_CONFIG[model];
  const queryField = config.queryField || model;
  const urlField = config.urlField;

  const ssgContent = data?.allBuilderModels?.[queryField]?.find(page => {
    if (!page?.data || page.published !== 'published' || !isPageActive(page)) {
      return false;
    }
    
    const pageUrl = page.data[urlField];
    

    if (model === 'landing-page') {
      const fullBuilderUrl = `/lp${pageUrl}`;
      return normalizeUrl(fullBuilderUrl) === normalizeUrl(url);
    }
    
    return normalizeUrl(pageUrl) === normalizeUrl(url);
  });

  return ssgContent?.content;
};

export function useBuilderFallback(url, model) {
  const data = useStaticQuery(graphql`
    query {
      allBuilderModels {
        page(options: { cachebust: true, limit: 1000000 }) {
          data {
            url
          }
          content
          published
          startDate
          endDate
        }
        landingPage(options: { cachebust: true, limit: 1000000 }) {
          data {
            pageUrl
          }
          content
          published
          startDate
          endDate
        }
      }
    }
  `);

  const ssgContent = findSSGContent(data, url, model);
  const isPreviewMode = builder.editingMode$.value || builder.preview;
  
  const [content, setContent] = useState(isPreviewMode ? null : ssgContent);
  const [isLoading, setIsLoading] = useState(isPreviewMode);

  useEffect(() => {
    let isMounted = true;
    
    const loadContent = async () => {
      try {
        if (isPreviewMode) {
          const dynamicContent = await getBuilderData(model, { url });
          
          if (isMounted) {
            setContent(dynamicContent?.data ? dynamicContent : null);
            setIsLoading(false);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Builder content error:', error);
          setContent(ssgContent);
          setIsLoading(false);
        }
      }
    };

    loadContent();
    
    return () => {
      isMounted = false;
    };
  }, [url, model, ssgContent, isPreviewMode]);

  return { content, isLoading };
}

// Enhanced HOC that works with both page and landing-page models
export function withBuilderFallback(StaticPageComponent, url, model = 'page') {
  return function EnhancedPageComponent(props) {
    const { content } = useBuilderFallback(url, model);

    if (content) {
      return (
        <main>
          <div className="hero py-0">
            <BuilderComponent
              model={model}
              content={content}
            />
          </div>
        </main>
      );
    }

    return <StaticPageComponent {...props} />;
  };
}